import apiService from "../../Services/APIService";
import { format, formatISO, parse } from "date-fns";
import { checkAPIResponse } from "../../Utils/APIResponseUtils";
import { getAuthData } from "../../Utils/AuthUtils";
import { countries, states } from "../../Components/Enums/DataEnums";
import { toast } from "sonner";
import { combineDateTime, getThreadNameFromTypeID, getTypeIdFromThread } from '../../Utils/Utilities';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import store from "../../Store/Store";


const buildThreadPayload = (threadData, operation, threadTypes) => {
  const state = store.getState();
  const isEdit = ['EDIT', 'UPDATE'].includes(operation);
  const isDelete = operation === 'DLT';
  const connection = state.connectionData?.data?.find(c => c.ConnID == threadData.connID);

  // Format target date - FIXED: Use dueDT instead of targetDT
  let targetDT = '';
  if (threadData.dueDT && !isDelete) {
    targetDT = threadData.dueDT.includes('T')
      ? format(new Date(threadData.dueDT), 'dd-MMM-yy')
      : threadData.dueDT;
  }

  // Format since date
  let sinceDT = '';
  if (threadData.sinceDT && !isDelete) {
    sinceDT = threadData.sinceDT.includes('T')
      ? format(new Date(threadData.sinceDT), 'dd-MMM-yy HH:mm:ss')
      : threadData.sinceDT;
  }

  // Format next task date/time using utility function
  let nextTaskDT = '';
  if (threadData.nextTaskDT && threadData.nextTaskTime && !isDelete) {
    nextTaskDT = combineDateTime(threadData.nextTaskDT, threadData.nextTaskTime) || '';
  }

  return {
    threadID: Number(threadData.threadID || threadData.ThreadID || threadData.actionID || 0) || 0,
    typeID: Number(threadData.typeID || threadData.TypeID || getTypeIdFromThread(threadData.threadType, threadTypes) || 1) || 1,
    changeType: operation === 'INQDONE' ? 'INQDONE' : operation === 'ADDDONEORD' ? 'ADDDONEORD' :
                operation === 'ORDDONE' ? 'ORDDONE' : (isDelete ? operation : (isEdit ? operation : 'ADD')),
    connID: Number(threadData.connID || threadData.ConnID || 0) || 0,
    contactPerson: threadData.contactPerson || threadData.ContactPerson || '',
    sinceDT: sinceDT || format(new Date(), 'dd-MMM-yy HH:mm:ss'),
    product: threadData.product || threadData.MainProduct || threadData.mainProduct || '',
    qty: threadData.quantity !== undefined && threadData.quantity !== null ? String(threadData.quantity) : (threadData.mainQty !== undefined && threadData.mainQty !== null ? String(threadData.mainQty) : (threadData.MainQty !== undefined && threadData.MainQty !== null ? String(threadData.MainQty) : '')),
    model: threadData.model || threadData.Model || '',
    source: threadData.source || threadData.Source || '',
    reference: threadData.reference || threadData.Reference || '',
    bizValue: threadData.businessValue !== undefined && threadData.businessValue !== null ? String(threadData.businessValue) : (threadData.bizProspect !== undefined && threadData.bizProspect !== null ? String(threadData.bizProspect) : (threadData.BizProspect !== undefined && threadData.BizProspect !== null ? String(threadData.BizProspect) : '0')),
    dueDT: targetDT || '', // This now properly maps from threadData.dueDT
    pocUserID: Number(threadData.pocUserID || threadData.POCUserID || 0) || 0,
    nextTaskDT: nextTaskDT || '',
    nextTaskType: threadData.nextTaskType || threadData.nextTaskTyp || '',
    nextTaskNotes: threadData.nextTaskNotes || threadData.nextTaskNote || '',
    notes: threadData.notes || threadData.Notes || '',
    email: connection?.Email || threadData.email || threadData.Email || '',
    isSendEmail: Boolean(threadData.shareByEmail || threadData.isSendEmail || false),
    currencySymbol: threadData.currencySymbol || '',
    commonThrdName: (threadData.typeID === 3
      ? getThreadNameFromTypeID(threadData.typeID, threadTypes)
      : getThreadNameFromTypeID(threadData.typeID, threadTypes, "SnSng")) || '',
    agencyName: state.agencyData?.data?.AgencyName || threadData.agencyName || '',
    bizName: connection?.BizName || threadData.bizName || threadData.BizName || '',
    customFields: typeof threadData.customFields === 'string' ? threadData.customFields : (threadData.customFields ? JSON.stringify(threadData.customFields) : (typeof threadData.CustomFields === 'string' ? threadData.CustomFields : (threadData.CustomFields ? JSON.stringify(threadData.CustomFields) : '[]'))),
    linkedThrID: (threadData.inquiryThreadID && Number(threadData.inquiryThreadID) !== Number(threadData.threadID || threadData.ThreadID)) ? Number(threadData.inquiryThreadID) : (Number(threadData.linkedThrID || threadData.LinkedThrID || 0) || 0),
    thrFile: threadData.thrFile || threadData.ThrFile || threadData.thrfile || '',
    secConnIDs: Array.isArray(threadData.secConnIDs) ? threadData.secConnIDs.join(',') : (threadData.secConnIDs || threadData.SecConnIDs || ''),
    currStageID: Number(threadData.stageID || threadData.currStageID || threadData.CurrStageID || 1) || 1,
    statusText: threadData.statusText || ''
  };
};

// Unified thread management function
export const manageThreadAPI = async (threadData, operation = 'ADD', threadTypes = []) => {
  try {
    const payload = buildThreadPayload(threadData, operation, threadTypes);
    const result = await apiService.sendChange(1, 'set', payload);
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error(`${operation} thread API error:`, error);
    throw error;
  }
};


// Build payload for stage movement operations
const buildStageUpdatePayload = (threadData, stageID, markAsDone = false) => {
  let doneDT = '';
  let nextTaskDT = '';

  // If stageID is 7 OR markAsDone is true, set doneDT
  if ((markAsDone || stageID === 7) && threadData.doneDT) {
    doneDT = threadData.doneDT.includes('T')
      ? format(new Date(threadData.doneDT), 'dd-MMM-yy HH:mm:ss')
      : threadData.doneDT;
  }
  // If no doneDT provided but stageID is 7, use current date/time
  else if (stageID === 7 && !threadData.doneDT) {
    doneDT = format(new Date(), 'dd-MMM-yy HH:mm:ss');
  }

  if (!markAsDone && stageID !== 7 && threadData.nextTaskDT && threadData.nextTaskTime) {
    nextTaskDT = combineDateTime(threadData.nextTaskDT, threadData.nextTaskTime) || '';
  }

  return {
    threadID: threadData.threadID || 0,
    typeID: threadData.typeID || 1,
    changeType: 'STGMV',
    stageID: stageID,
    pocUserID: threadData.pocUserID || 0,
    lastRecDT: null,
    lastRecType: '',
    lastRecUID: 0,
    lastRecNotes: '',
    lastRecLat: '',
    lastRecLon: '',
    nextTaskDT: (markAsDone || stageID === 7) ? null : nextTaskDT,
    nextTaskTyp: (markAsDone || stageID === 7) ? '' : (threadData.nextTaskType || ''),
    nextTaskUID: (markAsDone || stageID === 7) ? 0 : (threadData.nextTaskUID || 0),
    nextTaskNotes: (markAsDone || stageID === 7) ? '' : (threadData.nextTaskNotes || ''),
    doneDT: (markAsDone || stageID === 7) ? doneDT : null,
    doneNotes: (markAsDone || stageID === 7) ? (threadData.doneNotes || '') : '',
    shareByEmail: false,
    isCold: threadData?.isCold || false
  };
};

// Stage update API function
export const updateThreadStageAPI = async (threadData, stageID, markAsDone = false) => {
  try {
    const payload = buildStageUpdatePayload(threadData, stageID, markAsDone);
    const result = await apiService.sendChange(1, 'action', payload);
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error('Update thread stage API error:', error);
    throw error;
  }
};

export const saveInquiriesBulk = async (threadParams, source = 'EXCEL', typeID = 1) => {
  try {
    const authData = getAuthData();

    if (!authData) {
      throw new Error('Authentication data not found');
    }

    const formatSmallDateTime = (dateString) => {
      if (!dateString || dateString === 'null' || dateString === 'undefined') {
        return null;
      }

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return null;
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      } catch (error) {
        return null;
      }
    };

    // Process the data exactly as before
    const correctedParams = threadParams.map((row) => {
      const processedRow = {
        BizName: String(row.BizName || '').substring(0, 150),
        ContactName: String(row.ContactName || '').substring(0, 150),
        Contacts: String(row.Contacts || '[]'),
        Mobile: String(row.Mobile || '').substring(0, 15),
        CC: Number(parseInt(row.CC) || (parseInt(row.CountryID) === 1 ? 91 : 1)),
        Email: String(row.Email || '').substring(0, 255),
        POCUserID: Number(parseInt(row.POCUserID) || parseInt(authData.UserID) || 0),
        Source: String(row.Source || source || 'EXCEL').substring(0, 50),
        Notes: String(row.Notes || '').substring(0, 1000),
        City: String(row.City || '').substring(0, 50),
        StateID: Number(parseInt(row.StateID) || 0),
        State: String(row.State || '').substring(0, 50),
        CountryID: Number(parseInt(row.CountryID) || 1),
        Country: String(row.Country || '').substring(0, 50),
        TypeID: Number(parseInt(row.TypeID) || 1),
        ConnID: Number(parseInt(row.ConnID) || 0),
        Type: String(row.Type || 'INQ').substring(0, 20),
        MainProduct: String(row.MainProduct || row.Product || '').substring(0, 100),
        MainQty: String(row.MainQty || row.Requirements || row.Quantity || '').substring(0, 50),
        Items: String(row.Items || '[]'),
        DueDT: formatSmallDateTime(row.DueDT),
        CurrStage: String(row.CurrStage || 'New').substring(0, 50),
        ExtraFields: String(row.ExtraFields || '[]'),
        Model: String(row.Model || '').substring(0, 30),
        BizProspect: Number(parseFloat(row.BizProspect) || 0.0),
        Reference: String(row.Reference || '').substring(0, 20),
        SinceDT: formatSmallDateTime(row.SinceDT),
        Stage: Number(parseInt(row.Stage) || 1),
        SrcActionID: String(row.SrcThreadID || row.SrcActionID || '0').substring(0, 100),
        NextTask: String(row.NextTask || '').substring(0, 4000),
        NextTaskDT: formatSmallDateTime(row.NextTaskDT),
        NextTaskType: String(row.NextTaskType || '-').substring(0, 20)
      };

      Object.keys(processedRow).forEach(key => {
        if (processedRow[key] === undefined) {
          processedRow[key] = null;
        }
      });

      return processedRow;
    });

    const params = {
      threadParams: correctedParams,
      src: source,
      typeID: typeID
    }

    // SEND JUST THE FLAT ARRAY - no nesting!
    const result = await apiService.sendChangeForm(1, 'setBulk', params);
    checkAPIResponse(result);

    if (result.error) {
      throw new Error(result.message || 'API request failed');
    }

    return { success: true, message: 'All inquiries saved.', result };
  } catch (error) {
    throw error;
  }
};

// Thread field preferences update
export const updateThreadFieldPrefsAPI = async (preferences, threadTypeID, threadTypes = []) => {
  try {
    const typeId = typeof threadTypeID === 'number'
      ? threadTypeID
      : getTypeIdFromThread(threadTypeID, threadTypes);

    if (!typeId) {
      const availableTypes = threadTypes.map(t => t.Typ || t.name).join(', ');
      throw new Error(`Thread type not found: ${threadTypeID}. Available: ${availableTypes}`);
    }

    const configData = {
      changeType: "ENTRYPREFS",
      key: "THREADPREFS",
      value: "",
      values: JSON.stringify({
        TID: typeId,
        Prefs: {
          POC: preferences.Contact || false,
          Item: preferences.Model || false,
          Ref: preferences.Reference || false,
          Qty: preferences.Quantity || false,
          AsgnTo: preferences.AssignedTo || false,
          NextTsk: preferences.NextAction || false,
          Since: preferences.Since || false,
          Source: preferences.Source || false
        }
      })
    };

    const result = await apiService.sendChange(3, "set", configData, {
      isWeb: true,
    });
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error(`Failed to update thread preferences for TID ${threadTypeID}:`, error);
    throw error;
  }
};

//get propcessed Thread()

export const getThreadsAPI = async ({ statusID, threadTypeID, prdType, prdQty, year, month }) => {
  const payload = [{
    statusID,
    threadTypeID,
    prdType,
    prdQty,
    year,
    month
  }];
  const result = await apiService.getData(1, "get", payload);
  checkAPIResponse(result)
  return result
}

export const getProductAnalyticsAPI = async ({ threadTypeID, prdType, prdQty, year, month }) => {
  const payload = [{
    threadTypeID,
    prdType,
    prdQty,
    year,
    month
  }];
  const result = await apiService.getData(1, "getProductAnalysis", payload);
  checkAPIResponse(result)
  return result
}
export const getLossAnallyticsAPI = async ({ threadTypeID, prdType, prdQty, year, month, changeType }) => {
  const payload = [{
    threadTypeID,
    prdType,
    prdQty,
    year,
    month,
    changeType
  }];
  const result = await apiService.getData(1, "getLossAnalysis", payload);
  checkAPIResponse(result)
  return result
}

//map for countrycode
const countryCodeMap = Object.fromEntries(
  countries.map((c) => [
    c.CountryCode.toUpperCase(),
    { countryID: c.CountryID, country: c.Country, cc: c.CC },
  ])
);
const countryNameMap = Object.fromEntries(
  countries.map((c) => [
    c.Country.toUpperCase(),
    { countryID: c.CountryID, country: c.Country, cc: c.CC, countryCode: c.CountryCode },
  ])
);

export const getIntegrationData = async ({
  caseType,
  fromDT,
  toDT,
}) => {
  try {
    let importedData = [];
    let source = "";
    let typeID = 1; // Default to Inquiry

    switch (caseType) {
      case "indiamart":
        importedData = await IntgnIndiaMart({ fromDT, toDT });
        source = "IMRT";
        typeID = 1;
        break;
      case "tradeindia":
        importedData = await IntgnTradeIndia({ fromDT, toDT });
        source = "TI";
        typeID = 1;
        break;
      default:
        toast.error(`Integration ${caseType} not supported.`);
        return;
    }

    if (!importedData || importedData.length === 0) return;

    const failedToSavedData = await setImportedBulkData(importedData, source, typeID);
    return failedToSavedData;
  } catch (error) {
    toast.error(error.message || "Something went wrong while importing or saving data.");
  }
};

// IndiaMART
export const IntgnIndiaMart = async ({ fromDT, toDT }) => {
  const payload = [{ fromDT: format(fromDT, "yyyy-MM-dd"), toDT: format(toDT, "yyyy-MM-dd") }];
  const result = await apiService.getData(1, "getInquiriesIM", payload);

  const parsedUpdatePackage = JSON.parse(result.updatePackage);

  const errorTable = parsedUpdatePackage.Table[0];

  if (parseInt(errorTable.ErrCode) !== -1) {
    toast.error(errorTable.ErrMsg);
    return;
  }
  const indiaMartTableData = parsedUpdatePackage?.Table1?.[0]?.IndiaMartDT;

  if (!indiaMartTableData) {
    toast.error("No Response From IndiaMart");
    return;
  }

  const parsed = JSON.parse(indiaMartTableData);

  if (parsed.STATUS === "FAILURE") {
    toast.error(parsed.MESSAGE || "Failed to import data from IndiaMart");
    return;
  }

  if (!parsed.RESPONSE) {
    toast.error("No data returned from IndiaMart");
    return;
  }

  const rawData = parsed.RESPONSE;

  const preparedData = rawData.map((item) => {
    const [ccRaw = "", mobileRaw = ""] = (item.SENDER_MOBILE || item.mobile || "").split("-");
    const parsedCC = parseInt(ccRaw.replace("+", ""), 10) || 0;
    const isoCode = (item.SENDER_COUNTRY_ISO || item.country_iso || "").toUpperCase();

    const {
      countryID = 1,
      country = "India",
    } = countryCodeMap[isoCode] || {};

    const stateID =
      countryID === 1
        ? states.find(
          (s) =>
            s.CountryID === 1 &&
            s.State.toLowerCase() === (item.SENDER_STATE || "").toLowerCase()
        )?.StateID || 0
        : 0;

    return {
      bizName: item.SENDER_COMPANY,
      contactName: item.SENDER_NAME,
      mobile: mobileRaw,
      cc: parsedCC,
      email: item.SENDER_EMAIL,
      source: "IndiaMart",
      notes: (item.QUERY_MESSAGE || "").slice(0, 1000),
      city: item.SENDER_CITY,
      stateID: stateID,
      state: item.SENDER_STATE,
      countryID: countryID,
      country: country,
      mainProduct: item.QUERY_PRODUCT_NAME,
      sinceDT: formatISO(parse(item.QUERY_TIME, "yyyy-MM-dd HH:mm:ss", new Date())),
      srcThreadID: item.UNIQUE_QUERY_ID || '0'
    };
  });

  return preparedData;
};

export const IntgnTradeIndia = async ({ fromDT, toDT }) => {
  const payload = [{ fromDT: format(fromDT, "yyyy-MM-dd"), toDT: format(toDT, "yyyy-MM-dd") }];

  const result = await apiService.getData(1, "getInquiriesTI", payload);
  const parsedUpdatePackage = JSON.parse(result.updatePackage);
  const errorTable = parsedUpdatePackage.Table[0];

  if (parseInt(errorTable.ErrCode) !== -1) {
    toast.error(errorTable.ErrMsg);
    return;
  }

  const tradeIndiaDataTable = parsedUpdatePackage?.Table1?.[0]?.TradeIndiaDT;

  if (!tradeIndiaDataTable) {
    toast.error("No Response From Tradeindia");
    return [];
  }

  const parsedData = JSON.parse(tradeIndiaDataTable);

  const preparedData = parsedData.map((item) => {

    let numberStr = "";
    let cc = "";

    const currMobileStr = item.sender_mobile;
    const phone = parsePhoneNumberFromString(currMobileStr);

    if (phone && phone.isValid()) {
      cc = phone.countryCallingCode;
      numberStr = phone.nationalNumber;
    }

    const conName = (item.sender_country || "").toUpperCase();
    const { countryID = 1, } = countryNameMap[conName] || {};

    const stateID = countryID === 1 ? states.find((s) => s.State.toLowerCase() === (item.sender_state || "").toLowerCase())?.StateID || 0 : 0;
    let sinceDT = item?.generated_date ? item.generated_date : format(fromDT, "yyyy-MM-dd");
    let sinceTime = item?.generated_time ? item.generated_time : '00:00:00';
    const formattedSinceDT = format(parse(`${sinceDT} ${sinceTime}`, "dd MMMM yyyy HH:mm:ss", new Date()), "yyyy-MM-dd HH:mm:ss");

    return {
      bizName: item?.sender_co ? item.sender_co : '-',
      contactName: item?.sender_name ? item.sender_name : '',
      mobile: numberStr ?? "",
      cc: Number(cc) || 0,
      email: item?.sender_email && item?.sender_email !== "NA" ? item.sender_email : '',
      source: "TradeIndia",
      notes: item?.message ? (item.message || "").slice(0, 1000) : '',
      city: item?.sender_city ? item.sender_city : '',
      stateID: stateID || 0,
      state: item?.sender_state ? item.sender_state : '',
      countryID: countryID || 0,
      country: item?.sender_country ? item.sender_country : '',
      mainProduct: item?.product_name ? item.product_name : '',
      sinceDT: formattedSinceDT
    };
  });

  return preparedData;
};

export const setImportedBulkData = async (importedData, source, typeID) => {
  try {

    const preparedBulkData = importedData.map((item) => ({
      bizName: item.bizName || "",
      contactName: item.contactName || "",
      contacts: item.contacts || "[]",
      mobile: item.mobile || "",
      cc: item.cc || 91,
      email: item.email || "",
      pocUserID: 0,
      source: item.source || "",
      notes: item.notes || "",
      city: item.city || "",
      stateID: item.stateID || 0,
      state: item.state || "",
      countryID: item.countryID || 1,
      country: item.country || "India",
      typeID: item.typeID || 1,
      connID: item.connID || 0,
      type: item.type || "INQ",
      mainProduct: item.mainProduct || "",
      mainQty: item.mainQty || 0,
      items: item.items || "[]",
      dueDT: item.dueDT || null,
      currStage: item.currStage || "New",
      extraFields: item.extraFields || "[]",
      model: item.model || "",
      bizProspect: item.bizProspect || 0,
      reference: item.reference || "",
      sinceDT: item.sinceDT || null,
      stage: item.stage || 1,
      srcThreadID: item.srcThreadID || '0',
      nextTask: item.nextTask || "",
      nextTaskDT: item.nextTaskDT || null,
      nextTaskType: item.nextTaskType || "",
    }))

    let params = {
      src: source,
      typeID: typeID,
      threadParams: preparedBulkData
    }

    const setBulkRes = await apiService.sendChange(1, "setBulk", params);

    checkAPIResponse(setBulkRes);

    if (!setBulkRes.updatePackage) {
      toast.error(setBulkRes?.MESSAGE || "Failed to save imported entries.");
      return;
    }

    const parsedUpdate = JSON.parse(setBulkRes.updatePackage);
    const rawRetData = parsedUpdate?.Table?.[0]?.RetData;
    if (!rawRetData) return { failed: [] };

    const retDataArray = JSON.parse(rawRetData);
    let retThrData = retDataArray?.ThreadsData || [];
    const failedToSaveData = retThrData.filter((entry) => entry.IsError === true);

    const successCount = retThrData.length - failedToSaveData.length;

    if (successCount > 0) {
      toast.success(`${successCount} ${successCount === 1 ? "entry" : "entries"} imported.`);
    }

    return failedToSaveData;
  } catch (error) {
    toast.error("Something went wrong while saving imported data.");
    return;
  }
};

// Build payload for thread action operations (DONE/CANCEL/STAR)
const buildThreadActionPayload = (threadData, operation) => {
  const state = store.getState();
  const isDone = operation === 'DONE';
  const isCancel = operation === 'CANCEL';
  const isStar = operation === 'STAR';
  const threadTypes = state.masterData.threadTypes;
  const connection = state.connectionData?.data?.find(c => c.ConnID == (threadData.connID || threadData.ConnID));

  // Format done date
  let doneDT = '';
  if ((isDone || isCancel) && threadData.doneDT) {
    doneDT = threadData.doneDT.includes('T')
      ? format(new Date(threadData.doneDT), 'dd-MMM-yy HH:mm:ss')
      : threadData.doneDT;
  }

  if (isStar) {
    return {
      threadID: threadData.threadID || threadData.actionID || 0,
      typeID: threadData.typeID || 1,
      changeType: operation,
      stageID: 0,
      pocUserID: 0,
      lastRecDT: null,
      lastRecType: '',
      lastRecUID: 0,
      lastRecNotes: '',
      lastRecLat: '',
      lastRecLon: '',
      nextTaskDT: null,
      nextTaskTyp: '',
      nextTaskUID: 0,
      nextTaskNotes: '',
      doneDT: null,
      doneNotes: '',
      isStar: threadData.isStar
    };
  }

  const payload = {
    threadID: threadData.threadID || threadData.actionID || 0,
    typeID: threadData.typeID || 1,
    changeType: operation,
    stageID: threadData.stageID || 0,
    pocUserID: threadData.pocUserID || 0,
    lastRecDT: null,
    lastRecType: '',
    lastRecUID: 0,
    lastRecNotes: '',
    lastRecLat: '',
    lastRecLon: '',
    nextTaskDT: null,
    nextTaskTyp: '',
    nextTaskUID: 0,
    nextTaskNotes: '',
    doneDT: doneDT,
    doneNotes: threadData.doneNotes || threadData.cancellationReason || '',
    reference: threadData?.reference || threadData?.Reference || "",
    product: threadData?.mainProduct || threadData?.MainProduct || threadData?.product || "",
    connID: threadData?.connID || threadData?.ConnID || 0,
    contactPerson: threadData?.connectionData?.contactName || threadData?.connectionData?.ContactName || threadData?.contactPerson || threadData?.ContactPerson || connection?.ContactName || '',
    bizName: threadData?.connectionData?.bizName || threadData?.connectionData?.BizName || threadData?.bizName || threadData?.BizName || connection?.BizName || '',
    currencySymbol: threadData.currencySymbol,
    bizValue: threadData?.bizProspect || threadData?.BizProspect || threadData?.bizValue || 0,
    email: threadData?.connectionData?.email || threadData?.connectionData?.Email || threadData?.email || threadData?.Email || connection?.Email || '',
    mobile: threadData?.connectionData?.mobile || threadData?.connectionData?.Mobile || threadData?.mobile || threadData?.Mobile || connection?.Mobile || '',
    cc: threadData?.connectionData?.cc || threadData?.connectionData?.CC || threadData?.cc || threadData?.CC || connection?.CC || '',
    shareByEmail: threadData?.shareByEmail || false,
    shareByWhatsApp: threadData?.shareByWhatsApp || false,
    takeFeedback: threadData?.takeFeedback || false,
    commonThrdName: threadData.typeID === 3
      ? getThreadNameFromTypeID(threadData.typeID, threadTypes)
      : getThreadNameFromTypeID(threadData.typeID, threadTypes, "SnSng"),
    agencyName: state?.agencyData?.data?.AgencyName,
    isCold: threadData?.isCold || false,
    newStatusText: threadData?.newStatusText || '',
    showStatus: threadData?.showStatus ? 1 : 0
  };

  return payload;
};

// Build payload for thread reassignment
const buildReassignPayload = (threadData, newPOCUserID) => {
  return {
    threadID: threadData.threadID || threadData.actionID || 0,
    typeID: threadData.typeID || 1,
    changeType: 'ASGN',
    stageID: 0,
    pocUserID: newPOCUserID,
    lastRecDT: null,
    lastRecType: '',
    lastRecUID: 0,
    lastRecNotes: '',
    lastRecLat: '',
    lastRecLon: '',
    nextTaskDT: null,
    nextTaskTyp: '',
    nextTaskUID: 0,
    nextTaskNotes: '',
    doneDT: null,
    doneNotes: ''
  };
};
// Thread reassignment API function
export const reassignThreadAPI = async (threadData, newPOCUserID) => {
  try {
    const payload = buildReassignPayload(threadData, newPOCUserID);
    const result = await apiService.sendChange(1, 'action', payload);
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error('Reassign thread API error:', error);
    throw error;
  }
};

// Thread action API (DONE/CANCEL operations)
export const performThreadActionAPI = async (threadData, operation) => {
  try {
    const payload = buildThreadActionPayload(threadData, operation);
    const result = await apiService.sendChange(1, 'action', payload);
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error(`${operation} thread action API error:`, error);
    throw error;
  }
};

// Toggle thread star API function
export const toggleThreadStarAPI = async (threadData, isStar) => {
  try {
    const threadDataWithStar = { ...threadData, isStar };
    const payload = buildThreadActionPayload(threadDataWithStar, 'STAR');
    const result = await apiService.sendChange(1, 'action', payload);
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error('Toggle thread star API error:', error);
    throw error;
  }
};

export const bulkThreadActionAPI = async (threadArrIDs, changeType, pocUserID = null) => {
  try {
    const payload = {
      changeType: changeType,
      thrIDs: JSON.stringify(threadArrIDs.map((thrID) => ({ ID: thrID }))),
      pocUserID: pocUserID
    }
    const result = await apiService.sendChange(1, 'setBulkAction', payload);
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error(`Bulk thread action API error (${changeType}):`, error);
    throw error;
  }
};

// Thread request response API
export const requestResponseAPI = async (threadData) => {
  const state = store.getState();
  try {
    const payload = {
      ...threadData,
      agencyName: state.agencyData.data.AgencyName
    };
    // return;
    const result = await apiService.sendChange(1, 'reqRespEmail', payload);
    checkAPIResponse(result);
    return result;
  } catch (error) {
    console.error(`Request response email API error:`, error);
    throw error;
  }
};